import { mutate } from 'swr';
import { object, ObjectSchema, string } from 'yup';
import { api, ApiKey } from 'api';
import { useAuth, useToast } from 'hooks';
import { FormFieldProps, User } from 'types';
import { ProfileFormFields, ProfileFormFieldsNames } from './types';

export const profileSchema: ObjectSchema<ProfileFormFields> = object({
  [ProfileFormFieldsNames.FullName]: string().required('Full Name is required'),
});

export const defaultProfileFormValues: ProfileFormFields = {
  [ProfileFormFieldsNames.FullName]: '',
};
export const profileFormFieldsConfig: Record<ProfileFormFieldsNames, FormFieldProps> = {
  [ProfileFormFieldsNames.FullName]: {
    label: '* Full Name',
    placeholder: 'Full Name',
    name: ProfileFormFieldsNames.FullName,
    autoComplete: 'off',
    type: 'text',
  },
};

export function useApplyPaypalSubscription() {
  const { user, setUser } = useAuth();

  const toast = useToast();

  return async (subscriptionId: string) => {
    const res = await api.post<User>(`${ApiKey.Organization}/paypal-subscription`, {
      subscriptionId,
    });
    await mutate(ApiKey.User);

    await mutate(`${ApiKey.Users}/${user?.id}`);

    setUser?.(res.data);

    toast({
      message: 'Great! Your subscription has been updated. Enjoy your new features!',
      variant: 'success',
    });
  };
}

/**
 * Format a number with commas, or shorten to K/M suffix.
 * @param input — the number (or numeric string) to format
 * @returns formatted string
 */
export function formatNumber(input: number | string): string {
  // Parse the input
  const num = typeof input === 'string' ? parseFloat(input) : input;
  if (Number.isNaN(num)) {
    // If it wasn’t a valid number, return it as-is
    return String(input);
  }

  const abs = Math.abs(num);

  // Millions
  if (abs >= 1_000_000) {
    return `${(num / 1_000_000)
        .toFixed(1)
        .replace(/\.0$/, "")}M`;
  }

  // Thousands
  if (abs >= 1_000) {
    return `${(num / 1_000)
        .toFixed(1)
        .replace(/\.0$/, "")}K`;
  }

  // Under 1,000 → add commas
  return num.toLocaleString();
}
